import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';

interface HelpButtonProps {
  phone: string;
  email: string;
}

const getPhoneHref = (phone: string) => phone.replace(/[^\d+]/g, '');

export const HelpButton = ({ phone, email }: HelpButtonProps) => {
  const [open, setOpen] = useState(false);
  const trimmedPhone = phone.trim();
  const trimmedEmail = email.trim();
  const normalizedPhone = getPhoneHref(trimmedPhone);
  const hasPhone = trimmedPhone.length > 0 && normalizedPhone.length > 0;
  const hasEmail = trimmedEmail.length > 0;

  return (
    <>
      <HelpButtonRoot
        type='button'
        aria-label='Get Help'
        onClick={() => setOpen(true)}
      >
        <HelpIcon aria-hidden='true'>?</HelpIcon>
        Get Help
      </HelpButtonRoot>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <ContactDialogTitle>Contact Axis</ContactDialogTitle>
        <DialogContent>
          <ContactIntro variant='body2'>
            Need help? Contact Axis directly using one of the options below.
          </ContactIntro>

          {hasPhone ? (
            <ContactRow>
              <ContactLabel variant='body2'>Phone</ContactLabel>
              <ContactLink href={`tel:${normalizedPhone}`}>
                {trimmedPhone}
              </ContactLink>
            </ContactRow>
          ) : null}

          {hasEmail ? (
            <ContactRow>
              <ContactLabel variant='body2'>Email</ContactLabel>
              <ContactLink href={`mailto:${trimmedEmail}`}>
                {trimmedEmail}
              </ContactLink>
            </ContactRow>
          ) : null}

          {!hasPhone && !hasEmail ? (
            <Typography variant='body2'>
              Contact information is currently unavailable.
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ContactDialogTitle = styled(DialogTitle)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const ContactIntro = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const ContactRow = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const ContactLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightMedium,
}));

const ContactLink = styled('a')(({ theme }) => ({
  ...theme.typography.body1,
  display: 'block',
  marginTop: theme.spacing(0.25),
  color: theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightMedium,
  textDecoration: 'none',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

const HelpButtonRoot = styled('button')(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(5.625),
  bottom: theme.spacing(5),
  zIndex: theme.zIndex.speedDial,

  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),

  minHeight: theme.spacing(4.5),
  padding: theme.spacing(0.7, 1.6),

  border: `${theme.spacing(0.1875)} solid ${alpha(
    theme.palette.primary.main,
    0.35,
  )}`,
  borderRadius: theme.spacing(1.875),
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,

  ...theme.typography.button,
  cursor: 'pointer',
  fontFamily: theme.typography.fontFamily,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1,
  textTransform: 'none',

  boxShadow: `0 ${theme.spacing(0.125)} ${theme.spacing(0.25)} ${alpha(
    theme.palette.primary.main,
    0.12,
  )}`,
  overflow: 'visible',

  '&::after': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(2.25),
    right: theme.spacing(2.25),
    bottom: theme.spacing(-2.375),
    height: theme.spacing(0.75),
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.28),
    filter: `blur(${theme.spacing(0.5)})`,
    pointerEvents: 'none',
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    boxShadow: `0 ${theme.spacing(0.25)} ${theme.spacing(0.5)} ${alpha(
      theme.palette.primary.main,
      0.14,
    )}`,
  },

  [theme.breakpoints.down('sm')]: {
    right: theme.spacing(2),
    bottom: theme.spacing(2),
  },
}));

const HelpIcon = styled('span')(({ theme }) => ({
  ...theme.typography.body1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',

  width: theme.spacing(3),
  height: theme.spacing(3),

  border: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
  borderRadius: '50%',

  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1,
}));
